import moment from 'moment'
import { ReactElement } from 'react'

const statusMapping: Record<number, ReactElement> = {
  0: <span className='badge badge-light-info fw-bolder me-auto px-4 py-3'>Invited</span>,
  1: <span className='badge badge-light-primary fw-bolder me-auto px-4 py-3'>Accepted</span>,
  2: <span className='badge badge-light-danger fw-bolder me-auto px-4 py-3'>Declined</span>,
  3: <span className='badge badge-light-success fw-bolder me-auto px-4 py-3'>Confirmed</span>,
  4: (
    <span className='badge badge-light-secondary fw-bolder me-auto px-4 py-3 text-gray-600'>
      Rejected
    </span>

  ),
  5: <span className='badge badge-light-warning fw-bolder me-auto px-4 py-3'>Pending</span>,
  6: <span className='badge badge-light-dark fw-bolder me-auto px-4 py-3'>Cancelled</span>,
  7: <span className='badge badge-light-warning fw-bolder me-auto px-4 py-3'>Busy</span>,
  8: <span className='badge badge-light-warning fw-bolder me-auto px-4 py-3'>Non Compliant</span>,
  11: <span className='badge badge-light-warning fw-bolder me-auto px-4 py-3'>Unavailable</span>,
}

const assignStatusByName: Record<string, number> = {
  Invited: 0,
  InterpreterApproved: 1,
  InterpreterReject: 2,
  Assign: 3,
  Reject: 4,
  Pending: 5,
  AssignedButCancelled: 6,
  Busy: 7,
  NonCompliant: 8,
  TimeOff: 10,
  ManuallyUnavailable: 11,
}

const assignStatusNameByValue: Record<number, string> = {
  0: 'Invited',
  1: 'InterpreterApproved',
  2: 'InterpreterReject',
  3: 'Assign',
  4: 'Reject',
  5: 'Pending',
  6: 'AssignedButCancelled',
  7: 'Busy',
  8: 'NonCompliant',
  10: 'TimeOff',
  11: 'ManuallyUnavailable',
}

const resolveAssignStatus = (assignStatus: unknown): number | null => {
  if (assignStatus === null || assignStatus === undefined) {
    return null
  }

  if (typeof assignStatus === 'number' && !Number.isNaN(assignStatus)) {
    return assignStatus
  }

  if (typeof assignStatus === 'string') {
    const trimmed = assignStatus.trim()
    if (!trimmed) {
      return null
    }

    const numericStatus = Number(trimmed)
    if (!Number.isNaN(numericStatus)) {
      return numericStatus
    }

    return assignStatusByName[trimmed] ?? null
  }

  return null
}

const getAssignStatusBadge = (assignStatus: unknown) => {
  const resolvedStatus = resolveAssignStatus(assignStatus)
  if (resolvedStatus === null) {
    return null
  }

  return statusMapping[resolvedStatus] ?? null
}

const getAssignStatusName = (assignStatus: number) =>
  assignStatusNameByValue[assignStatus] ?? String(assignStatus)

const isApiSuccess = (response: any) =>
  String(response?.data?.status ?? response?.data?.Status ?? '').toUpperCase() === 'S'

const isInterpreterAvailableForAssignment = (assignStatus: unknown) => {
  const resolvedStatus = resolveAssignStatus(assignStatus)
  return resolvedStatus === 0 || resolvedStatus === 1
}

const isInterpreterManuallyUnavailable = (assignStatus: unknown) =>
  resolveAssignStatus(assignStatus) === 11

const downloadFile = async (base64URL: string, downloadFileName: string) => {
  try {
    // Fetch the file content
    const response = await fetch(base64URL)
    const data = await response.blob()

    // Create a URL for the Blob object
    const url = window.URL.createObjectURL(new Blob([data]))

    // Create a hidden anchor element with the download attribute
    const link = document.createElement('a')
    link.href = url
    link.download = downloadFileName // Set desired filename here

    // Programmatically click the anchor element to trigger the download
    link.click()

    // Revoke the URL to free up memory
    window.URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Error downloading file:', error)
  }
}

function getFileType(url: string): string | null {
  const extension = url.split('.').pop()?.toLowerCase() // Get the file extension and convert to lowercase

  // Check if the extension exists and is valid
  if (!extension || extension === url) {
    return null; // No valid extension found
  }

  return extension
}

function formatDateString(originalDateString: string) {
  // Create a Date object from the input time string
  let date = new Date(originalDateString)

  // Extract components of the date
  let year = date.getFullYear()
  let month = ('0' + (date.getMonth() + 1)).slice(-2) // Months are zero-based
  let day = ('0' + date.getDate()).slice(-2)
  let hours = ('0' + date.getHours()).slice(-2)
  let minutes = ('0' + date.getMinutes()).slice(-2)

  // Construct the desired format
  let outputTime = `${year}-${month}-${day}T${hours}:${minutes}`

  return outputTime
}

function formatDuration(allSeconds: number) {
  const duration = moment.duration(allSeconds, 'seconds')

  // Extract the number of days, hours, and minutes
  const days = Math.floor(duration.asDays())
  const hours = duration.hours()
  const minutes = duration.minutes()
  const seconds = duration.seconds()

  // Create a formatted string
  let fromateDate = '0 Day'
  let formattedDuration = ''

  if (days > 0) {
    fromateDate = `${days} Day${days > 1 ? 's' : ''} `
  }
  if (hours > 0 || days > 0) {
    // Include hours if there's a day or if hours > 0
    formattedDuration += `${hours} hour${hours > 1 ? 's' : ''} `
  }
  if (minutes > 0 || hours > 0 || days > 0) {
    // Include minutes if there's an hour or day or if minutes > 0
    formattedDuration += ` ${minutes} minute${minutes > 1 ? 's' : ''}`
  }
  if (seconds > 0 || minutes > 0 || hours > 0 || days > 0) {
    // Include minutes if there's an hour or day or if minutes > 0
    formattedDuration += ` ${seconds} second${seconds > 1 ? 's' : ''}`
  }

  return { fromateDate: fromateDate, formattedDuration: formattedDuration }
}

function getTotalAppointments(userType: string | undefined, appointmentStatusCount: any) {
  let total = 0
  if (userType === 'SYSTEM') {
    total =
      parseInt(appointmentStatusCount?.total) 
  }
  if (userType === 'CONSUMER') {
    total =
      parseInt(appointmentStatusCount?.open) +
      parseInt(appointmentStatusCount?.readyToAssign) +
      parseInt(appointmentStatusCount?.confirmed) +
      parseInt(appointmentStatusCount?.noInterpreter) +
      parseInt(appointmentStatusCount?.abandoned) +
      parseInt(appointmentStatusCount?.cancelled) +
      parseInt(appointmentStatusCount?.completed)
  }
  if (userType === 'INTERPRETER') {
    total =
      parseInt(appointmentStatusCount?.open) +
      parseInt(appointmentStatusCount?.readyToAssign) +
      parseInt(appointmentStatusCount?.confirmed) +
      parseInt(appointmentStatusCount?.abandoned) +
      parseInt(appointmentStatusCount?.cancelled) +
      parseInt(appointmentStatusCount?.completed)
  }
  return total
}

export {
  statusMapping,
  resolveAssignStatus,
  getAssignStatusBadge,
  getAssignStatusName,
  isApiSuccess,
  isInterpreterAvailableForAssignment,
  isInterpreterManuallyUnavailable,
  downloadFile,
  getFileType,
  formatDateString,
  formatDuration,
  getTotalAppointments,
}
